/**
 * WHEAFREE - Auto Add Free Sample to Cart
 * ========================================
 * Automatically adds a free sample when cart value is ₹500 or above.
 * - Only 1 sample allowed per cart
 * - Sample removed if cart drops below ₹500
 * - User can manually remove sample (remembered in localStorage)
 */

(function () {
    'use strict';

    // ==================== CONFIGURATION ====================
    var CONFIG = {
        VARIANT_ID: '51654764167487',           // Free Sample variant ID
        MIN_CART_VALUE: 50000,                   // ₹500 in paise (Shopify stores prices in paise)
        STORAGE_KEY: 'wheafree_sample_removed',  // localStorage key
        DEBUG: true                              // Console logging
    };
    // =======================================================

    // State
    var state = {
        sampleInCart: false,
        sampleKey: null,
        cartValue: 0,
        isProcessing: false,
        initialized: false
    };

    // Utility: Console logging
    function log(message, type) {
        if (!CONFIG.DEBUG) return;
        var prefix = '[Free Sample] ';
        if (type === 'error') {
            console.error(prefix + message);
        } else if (type === 'warn') {
            console.warn(prefix + message);
        } else {
            console.log(prefix + message);
        }
    }

    // Utility: Format price
    function formatPrice(paise) {
        return '₹' + (paise / 100).toFixed(2);
    }

    // Check if user has manually removed the sample
    function userRemovedSample() {
        return localStorage.getItem(CONFIG.STORAGE_KEY) === 'true';
    }

    // Mark sample as removed by user
    function markAsRemovedByUser() {
        localStorage.setItem(CONFIG.STORAGE_KEY, 'true');
        log('User removed sample. Will not auto-add again this session.');
    }

    // Inject CSS to hide quantity controls for free sample
    function injectCSS() {
        var id = CONFIG.VARIANT_ID;
        var css = `
            /* ===== HIDE QUANTITY CONTROLS FOR FREE SAMPLE ===== */
            
            /* Cart Page - Table Row */
            tr[data-key*="${id}"] .pro-quantity,
            tr[data-key*="${id}"] .cart-plus-minus,
            tr[data-key*="${id}"] .cart-product-quantity,
            tr[data-key*="${id}"] input[name="updates[]"],
            
            /* Cart Page - Cart Item */
            .cart-item[data-variant-id="${id}"] .quantity,
            .cart-item[data-variant-id="${id}"] .cart-qty-box,
            .cart-item[data-variant-id="${id}"] .cart-plus-minus,
            .cart-item[data-variant-id="${id}"] .qtybutton,
            .cart-item[data-variant-id="${id}"] input[name="updates[]"],
            
            /* Mini Cart */
            [data-id="${id}"] .quantity-selector,
            [data-variant-id="${id}"] .cart-plus-minus,
            .mini-cart-item[data-variant-id="${id}"] .cart-plus-minus,
            
            /* Product Page */
            [data-product-id="${id}"] .quantity,
            form[data-variant-id="${id}"] .quantity,
            [data-variant="${id}"] .quantity-wrapper,
            [data-variant-id="${id}"] .quantity-wrapper,
            
            /* AJAX Cart Drawer */
            [data-cart-item-id*="${id}"] .quantity,
            .cart-drawer-item[data-id="${id}"] .quantity {
                display: none !important;
                visibility: hidden !important;
                pointer-events: none !important;
            }
        `;

        var style = document.createElement('style');
        style.id = 'wheafree-sample-css';
        style.textContent = css;
        document.head.appendChild(style);
        log('CSS injected');
    }

    // Get cart data from Shopify
    function getCart() {
        return fetch('/cart.js?_=' + Date.now(), {
            method: 'GET',
            credentials: 'same-origin'
        })
            .then(function (response) {
                if (!response.ok) throw new Error('Failed to fetch cart');
                return response.json();
            });
    }

    // Add item to cart
    function addToCart(variantId, quantity) {
        return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
        })
            .then(function (response) {
                if (!response.ok) throw new Error('Failed to add to cart');
                return response.json();
            });
    }

    // Change item quantity in cart
    function changeCartItem(lineItemKey, quantity) {
        return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineItemKey, quantity: quantity })
        })
            .then(function (response) {
                if (!response.ok) throw new Error('Failed to change cart');
                return response.json();
            });
    }

    // Calculate cart value EXCLUDING the free sample
    function calculateCartValueWithoutSample(cart) {
        var total = 0;
        cart.items.forEach(function (item) {
            if (String(item.variant_id) !== String(CONFIG.VARIANT_ID)) {
                total += item.line_price;
            }
        });
        return total;
    }

    // Find sample in cart
    function findSampleInCart(cart) {
        return cart.items.find(function (item) {
            return String(item.variant_id) === String(CONFIG.VARIANT_ID);
        });
    }

    // Update internal state from cart
    function updateState(cart) {
        var sample = findSampleInCart(cart);
        state.sampleInCart = !!sample;
        state.sampleKey = sample ? sample.key : null;
        state.cartValue = calculateCartValueWithoutSample(cart);

        log('State updated: Cart=' + formatPrice(state.cartValue) +
            ', Sample=' + (state.sampleInCart ? 'YES (qty:' + sample.quantity + ')' : 'NO'));

        return sample;
    }

    // Refresh the page if on cart page
    function refreshIfOnCartPage() {
        if (window.location.pathname.includes('/cart')) {
            log('Refreshing cart page...');
            setTimeout(function () { location.reload(); }, 100);
        }
    }

    // Update mini cart UI (trigger theme's cart update)
    function triggerCartUpdate() {
        // Dispatch custom events that themes might listen to
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Also try to trigger Shopify's cart update
        if (window.Shopify && window.Shopify.getCart) {
            window.Shopify.getCart();
        }
    }

    // ==================== MAIN LOGIC ====================

    function processCart() {
        // Prevent concurrent processing
        if (state.isProcessing) {
            log('Already processing, skipping...', 'warn');
            return Promise.resolve();
        }

        // Skip if user manually removed sample
        if (userRemovedSample()) {
            log('User previously removed sample. Skipping.');
            return Promise.resolve();
        }

        state.isProcessing = true;

        return getCart()
            .then(function (cart) {
                var sample = updateState(cart);
                var meetsMinimum = state.cartValue >= CONFIG.MIN_CART_VALUE;

                log('Cart value: ' + formatPrice(state.cartValue) +
                    ' (Min: ' + formatPrice(CONFIG.MIN_CART_VALUE) + ')' +
                    ' | Qualifies: ' + (meetsMinimum ? 'YES' : 'NO'));

                // CASE 1: Cart is below ₹500 AND sample is in cart -> REMOVE
                if (!meetsMinimum && state.sampleInCart) {
                    log('Cart below minimum. Removing sample...');
                    return changeCartItem(state.sampleKey, 0)
                        .then(function () {
                            log('Sample removed (cart below ₹500)');
                            state.sampleInCart = false;
                            state.sampleKey = null;
                            triggerCartUpdate();
                            refreshIfOnCartPage();
                        });
                }

                // CASE 2: Cart is ₹500+ AND sample NOT in cart -> ADD
                if (meetsMinimum && !state.sampleInCart) {
                    log('Cart qualifies! Adding sample...');
                    return addToCart(CONFIG.VARIANT_ID, 1)
                        .then(function () {
                            log('Sample added successfully!');
                            state.sampleInCart = true;
                            triggerCartUpdate();
                            refreshIfOnCartPage();
                        });
                }

                // CASE 3: Sample quantity is more than 1 -> FIX TO 1
                if (state.sampleInCart && sample && sample.quantity > 1) {
                    log('Sample quantity too high (' + sample.quantity + '). Fixing to 1...');
                    return changeCartItem(state.sampleKey, 1)
                        .then(function () {
                            log('Sample quantity fixed to 1');
                            triggerCartUpdate();
                            refreshIfOnCartPage();
                        });
                }

                // CASE 4: Everything is correct
                log('Cart is properly configured.');
                return Promise.resolve();
            })
            .catch(function (error) {
                log('Error: ' + error.message, 'error');
            })
            .finally(function () {
                state.isProcessing = false;
            });
    }

    // ==================== INTERCEPTION ====================

    // Intercept fetch requests
    function interceptFetch() {
        var originalFetch = window.fetch;

        window.fetch = function (url, options) {
            var urlString = typeof url === 'string' ? url : (url && url.url ? url.url : '');

            // Block manual adds of the sample if already in cart
            if (urlString.includes('/cart/add') && !state.isProcessing) {
                if (options && options.body) {
                    try {
                        var bodyData = typeof options.body === 'string'
                            ? JSON.parse(options.body)
                            : null;

                        if (bodyData) {
                            var tryingToAddSample =
                                String(bodyData.id) === String(CONFIG.VARIANT_ID) ||
                                (bodyData.items && bodyData.items.some(function (i) {
                                    return String(i.id) === String(CONFIG.VARIANT_ID);
                                }));

                            if (tryingToAddSample && state.sampleInCart) {
                                log('BLOCKED: Cannot add sample again (already in cart)');
                                return Promise.resolve(new Response(
                                    JSON.stringify({ message: 'Sample already in cart' }),
                                    { status: 422, statusText: 'Unprocessable Entity' }
                                ));
                            }
                        }
                    } catch (e) { }
                }
            }

            // Execute original fetch
            return originalFetch.apply(this, arguments)
                .then(function (response) {
                    // After cart operations, re-process
                    if (urlString.includes('/cart/add') ||
                        urlString.includes('/cart/change') ||
                        urlString.includes('/cart/update') ||
                        urlString.includes('/cart/clear')) {

                        // Check if sample was manually removed
                        if (urlString.includes('/cart/change') && options && options.body) {
                            try {
                                var data = JSON.parse(options.body);
                                if (data.quantity === 0 &&
                                    (String(data.id).includes(CONFIG.VARIANT_ID) ||
                                        data.id === state.sampleKey)) {
                                    markAsRemovedByUser();
                                    return response;
                                }
                            } catch (e) { }
                        }

                        // Re-process cart after delay
                        if (!state.isProcessing) {
                            setTimeout(processCart, 500);
                        }
                    }
                    return response;
                });
        };

        log('Fetch interceptor installed');
    }

    // Intercept XMLHttpRequest
    function interceptXHR() {
        var originalOpen = XMLHttpRequest.prototype.open;
        var originalSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (method, url) {
            this._url = url;
            this._method = method;
            return originalOpen.apply(this, arguments);
        };

        XMLHttpRequest.prototype.send = function (body) {
            var self = this;

            // Listen for cart operations
            this.addEventListener('load', function () {
                if (self._url && (
                    self._url.includes('/cart/add') ||
                    self._url.includes('/cart/change') ||
                    self._url.includes('/cart/update'))) {

                    if (!state.isProcessing) {
                        setTimeout(processCart, 500);
                    }
                }
            });

            return originalSend.apply(this, arguments);
        };

        log('XHR interceptor installed');
    }

    // Handle removal via link clicks
    function handleLinkClicks() {
        document.addEventListener('click', function (e) {
            var link = e.target.closest('a[href*="/cart/change"]');
            if (link && link.href.includes('quantity=0')) {
                // Check if this removes the sample
                if (link.href.includes('line=') || state.sampleKey) {
                    // Wait for navigation or AJAX
                    setTimeout(function () {
                        getCart().then(function (cart) {
                            if (!findSampleInCart(cart)) {
                                markAsRemovedByUser();
                            }
                        });
                    }, 1000);
                }
            }
        });

        log('Click handler installed');
    }

    // Periodic check as backup
    function startPeriodicCheck() {
        setInterval(function () {
            if (!state.isProcessing && !userRemovedSample()) {
                processCart();
            }
        }, 5000); // Check every 5 seconds

        log('Periodic check started (every 5s)');
    }

    // ==================== INITIALIZATION ====================

    function init() {
        if (state.initialized) return;
        state.initialized = true;

        log('Initializing...');
        log('Variant ID: ' + CONFIG.VARIANT_ID);
        log('Min Cart Value: ' + formatPrice(CONFIG.MIN_CART_VALUE));

        // Setup
        injectCSS();
        interceptFetch();
        interceptXHR();
        handleLinkClicks();

        // Initial processing
        processCart().then(function () {
            startPeriodicCheck();
            log('Initialization complete!');
        });
    }

    // Start when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();